import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import { supabase } from '../config/supabase';
import CustomerHeader from '../components/CustomerHeader';
import VariationModal from '../components/VariationModal';
import { getRestaurantInfo } from '../utils/restaurantInfo';

const Home = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(() => {
    // Load cart from localStorage on mount
    try {
      const savedCart = localStorage.getItem('restaurantCart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = window.innerWidth < 768 ? 10 : 12;
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState({
    size: null,
    spice: null,
    addons: []
  });

  // Load delivery address - prioritize customer address, fallback to restaurant address from settings
  useEffect(() => {
    const loadDeliveryAddress = async () => {
      try {
        // First, try to get customer's saved address
        const customerData = localStorage.getItem('customer');
        if (customerData) {
          const customer = JSON.parse(customerData);
          if (customer.delivery_address) {
            setDeliveryAddress(customer.delivery_address);
            return; // Customer is logged in and has address, use it
          }
        }
        
        // If customer is logged out or has no saved address, fetch restaurant address from settings
        const { data, error } = await getRestaurantInfo();
        if (!error && data && data.address) {
          setDeliveryAddress(data.address);
        } else {
          // Fallback to empty if restaurant address not found
          setDeliveryAddress('');
        }
      } catch (error) {
        console.error('Error loading delivery address:', error);
        // Fallback to empty on error
        setDeliveryAddress('');
      }
    };
    
    loadDeliveryAddress();
    
    // Listen for customer data updates
    const handleStorageChange = () => {
      loadDeliveryAddress();
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Promotional slides data
  const slides = [
    {
      heading: 'Fresh Ingredients, Great Taste',
      subheading: 'Experience the finest flavors crafted with care and passion',
      cta: 'Explore Menu',
      icon: '🍽️'
    },
    {
      heading: 'New Menu Items',
      subheading: 'Discover our latest culinary creations and seasonal favorites',
      cta: 'Try Now',
      icon: '✨'
    },
    {
      heading: 'Quality You Can Trust',
      subheading: 'Every dish is prepared fresh daily with premium ingredients',
      cta: 'Order Now',
      icon: '⭐'
    }
  ];


  // Fetch categories
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch menu items
  useEffect(() => {
    fetchMenuItems();
  }, [selectedCategory]);

  // Filter items based on search and category
  useEffect(() => {
    let filtered = menuItems;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query) ||
        item.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredItems(filtered);
    // Reset to first page when search or category changes
    setCurrentPage(1);
  }, [menuItems, selectedCategory, searchQuery]);

  // Auto-scroll slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev >= slides.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [currentSlide]);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const fetchCategories = async () => {
    try {
      // Fetch active categories from Supabase
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching categories:', error);
        // Fallback to default categories
        setCategories([
          { id: 'all', name: 'All', icon: '🍽️' },
          { id: 'pizza', name: 'Pizza', icon: '🍕' },
          { id: 'burgers', name: 'Burgers', icon: '🍔' },
          { id: 'chicken', name: 'Chicken', icon: '🍗' },
          { id: 'chinese', name: 'Chinese', icon: '🥡' },
          { id: 'drinks', name: 'Drinks', icon: '🥤' },
          { id: 'desserts', name: 'Desserts', icon: '🍰' }
        ]);
        return;
      }

      if (data && data.length > 0) {
        // Format categories from Supabase
        const formatted = data.map(cat => ({
          id: cat.id,
          name: cat.name,
          icon: cat.icon || '🍽️'
        }));
        // Add "All" category at the beginning
        setCategories([{ id: 'all', name: 'All', icon: '🍽️' }, ...formatted]);
      } else {
        // If no categories exist in Supabase, use defaults
        setCategories([
          { id: 'all', name: 'All', icon: '🍽️' },
          { id: 'pizza', name: 'Pizza', icon: '🍕' },
          { id: 'burgers', name: 'Burgers', icon: '🍔' },
          { id: 'chicken', name: 'Chicken', icon: '🍗' },
          { id: 'chinese', name: 'Chinese', icon: '🥡' },
          { id: 'drinks', name: 'Drinks', icon: '🥤' },
          { id: 'desserts', name: 'Desserts', icon: '🍰' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to default categories on error
      setCategories([
        { id: 'all', name: 'All', icon: '🍽️' },
        { id: 'pizza', name: 'Pizza', icon: '🍕' },
        { id: 'burgers', name: 'Burgers', icon: '🍔' },
        { id: 'chicken', name: 'Chicken', icon: '🍗' },
        { id: 'chinese', name: 'Chinese', icon: '🥡' },
        { id: 'drinks', name: 'Drinks', icon: '🥤' },
        { id: 'desserts', name: 'Desserts', icon: '🍰' }
      ]);
    }
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('menu_items')
        .select('*')
        .eq('is_available', true);

      if (selectedCategory !== 'All') {
        query = query.eq('category', selectedCategory);
      }

      // Order by featured first, then by created date
      query = query.order('is_featured', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Supabase query error:', error);
        throw error;
      }

      // Transform data to match our format
      const transformed = (data || []).map(item => {
        const customizations = item.customizations || {};
        const sizes = customizations.sizes || [];
        const spiceLevels = customizations.spiceLevels || [];
        const addons = customizations.addons || [];

        // Transform addons to ensure they have IDs
        const transformedAddons = addons.map((addon, index) => ({
          id: addon.id || `addon-${index}`,
          name: addon.name || '',
          description: addon.description || '',
          price: parseFloat(addon.price) || 0,
          icon: addon.icon || '➕'
        }));

        // Transform sizes
        const transformedSizes = sizes.map(size => ({
          name: size.name || '',
          price: parseFloat(size.price) || 0
        }));

        return {
          id: item.id,
          name: item.name,
          description: item.description || '',
          category: item.category || 'Other',
          price: parseFloat(item.price) || 0,
          originalPrice: item.original_price ? parseFloat(item.original_price) : null,
          discountPercentage: item.discount_percentage || 0,
          imageUrl: item.image_url || item.images_urls?.[0] || '',
          rating: parseFloat(item.rating) || 0,
          reviewsCount: item.reviews_count || 0,
          isVegetarian: item.is_vegetarian || false,
          isVegan: item.is_vegan || false,
          tags: item.tags || [],
          isFeatured: item.is_featured || false,
          isPopular: item.is_popular || false,
          isNew: item.is_new || false,
          isSpicy: item.tags?.some(tag => tag.toLowerCase().includes('spicy')) || false,
          sizes: transformedSizes,
          spiceLevels: spiceLevels,
          addons: transformedAddons
        };
      });

      console.log('Fetched menu items from Supabase:', transformed.length);
      setMenuItems(transformed);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const hasVariations = (item) => {
    const hasSizes = item.sizes && item.sizes.length > 0;
    const hasSpiceLevels = item.spiceLevels && item.spiceLevels.length > 0;
    const hasAddons = item.addons && item.addons.length > 0;
    return hasSizes || hasSpiceLevels || hasAddons;
  };

  const handleAddToCartClick = (item) => {
    // Always check if item has variations - show modal if any variations exist
    const hasSizes = item.sizes && item.sizes.length > 0;
    const hasSpiceLevels = item.spiceLevels && item.spiceLevels.length > 0;
    const hasAddons = item.addons && item.addons.length > 0;
    
    if (hasSizes || hasSpiceLevels || hasAddons) {
      // Show variation modal with all available variations
      setSelectedItem(item);
      // Set default selections
      const defaultSize = hasSizes && item.sizes[0] ? item.sizes[0].name : null;
      const defaultSpice = hasSpiceLevels && item.spiceLevels[0] ? item.spiceLevels[0] : null;
      setSelectedVariations({
        size: defaultSize,
        spice: defaultSpice,
        addons: []
      });
      setShowVariationModal(true);
    } else {
      // No variations, add directly to cart
      addToCart(item);
    }
  };

  const addToCart = (item, variations = null) => {
    setCart(prevCart => {
      // Create cart item with variations if provided
      const cartItem = {
        ...item,
        quantity: 1,
        selectedSize: variations?.size || null,
        selectedSpice: variations?.spice || null,
        selectedAddons: variations?.addons || []
      };

      // Calculate price with variations
      let finalPrice = item.price;
      if (variations?.size && item.sizes) {
        const selectedSizeObj = item.sizes.find(s => s.name === variations.size);
        if (selectedSizeObj) {
          finalPrice = selectedSizeObj.price || item.price;
        }
      }
      if (variations?.addons && variations.addons.length > 0) {
        const addonsTotal = variations.addons.reduce((sum, addonId) => {
          const addon = item.addons.find(a => a.id === addonId);
          return sum + (addon ? addon.price : 0);
        }, 0);
        finalPrice += addonsTotal;
      }
      cartItem.finalPrice = finalPrice;
      cartItem.price = finalPrice;

      // Check if same item with same variations already exists
      const existingItem = prevCart.find(cartItem => 
        cartItem.id === item.id &&
        cartItem.selectedSize === (variations?.size || null) &&
        cartItem.selectedSpice === (variations?.spice || null) &&
        JSON.stringify(cartItem.selectedAddons?.sort()) === JSON.stringify((variations?.addons || []).sort())
      );

      let newCart;
      if (existingItem) {
        newCart = prevCart.map(c =>
          c.id === item.id &&
          c.selectedSize === (variations?.size || null) &&
          c.selectedSpice === (variations?.spice || null) &&
          JSON.stringify(c.selectedAddons?.sort()) === JSON.stringify((variations?.addons || []).sort())
            ? { ...c, quantity: c.quantity + 1 }
            : c
        );
      } else {
        newCart = [...prevCart, cartItem];
      }

      // Save to localStorage
      localStorage.setItem('restaurantCart', JSON.stringify(newCart));
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('cartUpdated'));
      return newCart;
    });
    showSuccessNotification('Item added to cart!');
  };

  const handleVariationConfirm = () => {
    if (selectedItem) {
      addToCart(selectedItem, selectedVariations);
      setShowVariationModal(false);
      setSelectedItem(null);
      setSelectedVariations({ size: null, spice: null, addons: [] });
    }
  };

  const showSuccessNotification = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 2000);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev >= slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev <= 0 ? slides.length - 1 : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get paginated items
  const paginatedItems = filteredItems.slice(0, currentPage * itemsPerPage);
  const hasMore = filteredItems.length > paginatedItems.length;

  const loadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  return (
    <div className="home-page">
      {/* Universal Customer Header */}
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        cartItemCount={getCartItemCount()} 
        onCartClick={() => navigate('/cart')}
      />

      {/* Promotional Banner Slider */}
      <section className="slider-section">
        <div className="slider-container">
          <div
            className="slider-wrapper"
            style={{ transform: `translateX(calc(-${currentSlide} * 100%))` }}
          >
            {slides.map((slide, index) => (
              <div key={index} className="slide">
                <div className="slide-content">
                  <div className="slide-content-left">
                    <h2 className="slide-heading">{slide.heading}</h2>
                    <p className="slide-subheading">{slide.subheading}</p>
                    <button className="slide-cta-button">{slide.cta}</button>
                  </div>
                  <div className="slide-content-right">
                    <div className="slide-decorative-icon">{slide.icon}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button className="slider-arrow slider-arrow-left" onClick={prevSlide} aria-label="Previous slide">
            &lt;
          </button>
          <button className="slider-arrow slider-arrow-right" onClick={nextSlide} aria-label="Next slide">
            &gt;
          </button>

          {/* Navigation Dots */}
          <div className="slider-dots">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Hero Section */}
          <section className="hero-section">
        <div className="hero-container">
          <div className="delivery-address-card">
            <span className="address-icon">📍</span>
            <div className="address-text-container">
              <span className="address-label">Delivering to:</span>
              <span className="address-value">{deliveryAddress}</span>
            </div>
          </div>

          {/* Mobile Search Bar */}
          <div className="mobile-search-bar">
            <div className="mobile-search-container">
              <div className="mobile-search-icon">🔍</div>
              <input
                type="text"
                className="mobile-search-input"
                placeholder="Search for dishes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter Section */}
      <section className="category-section">
        <div className="category-container">
          <h2 className="section-title">Categories</h2>
          <div className="categories-scroll">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-button ${selectedCategory === category.name ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.name)}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-text">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="main-content-section">
        <div className="content-container">
          <div className="section-header">
            <div className="header-left">
              <h2 className="section-title">Menu Items</h2>
              <p className="section-subtitle">Discover our delicious offerings</p>
            </div>
          </div>

          {/* Menu Items Grid */}
          {loading ? (
            <div className="menu-items-grid">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="skeleton-card">
                  <div className="skeleton-image" />
                  <div className="skeleton-title" />
                  <div className="skeleton-description" />
                  <div className="skeleton-price" />
                </div>
              ))}
            </div>
          ) : paginatedItems.length > 0 ? (
            <>
              <div className="menu-items-grid">
                {paginatedItems.map((item) => (
                  <div 
                    key={item.id} 
                    className="menu-item-card"
                    onClick={() => navigate(`/item/${item.id}`)}
                  >
                    <div className="item-image-container">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="item-image"
                        />
                      ) : (
                        <div className="item-image-placeholder">🍽️</div>
                      )}
                      <div className="badges-container">
                        {item.isPopular && (
                          <span className="badge badge-popular">Popular</span>
                        )}
                        {item.isNew && (
                          <span className="badge badge-new">New</span>
                        )}
                        {item.isSpicy && (
                          <span className="badge badge-spicy">Spicy</span>
                        )}
                      </div>
                    </div>
                    <div className="card-details">
                      <h3 className="item-name">{item.name}</h3>
                      <span className="category-tag">{item.category}</span>
                      <p className="item-description">{item.description}</p>
                      <div className="price-action-row">
                        <div className="price-container">
                          {item.originalPrice && item.originalPrice > item.price && (
                            <span className="original-price">Rs. {item.originalPrice.toFixed(2)}</span>
                          )}
                          <span className="current-price">Rs. {item.price.toFixed(2)}</span>
                          {item.discountPercentage > 0 && (
                            <span className="discount-badge">
                              {item.discountPercentage}% off
                            </span>
                          )}
                        </div>
                        <button
                          className="add-to-cart-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCartClick(item);
                          }}
                          aria-label={`Add ${item.name} to cart`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="load-more-section">
                  <button className="load-more-button" onClick={loadMore}>
                    Load More
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3 className="empty-title">
                {searchQuery.trim() ? 'No items found for your search' : 'No items found'}
              </h3>
              <p className="empty-subtitle">
                {searchQuery.trim() 
                  ? `No items match "${searchQuery}". Try a different search term or clear the search.`
                  : 'Try adjusting your filters or category selection'}
              </p>
              {searchQuery.trim() && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchQuery('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
            </div>
          </section>

      {/* Floating Cart Button (Mobile Only) */}
      <button
        className="floating-cart-button"
        onClick={() => navigate('/cart')}
        aria-label="Open cart"
      >
        <span className="floating-cart-icon">🛒</span>
        {getCartItemCount() > 0 && (
          <span className="floating-cart-badge">{getCartItemCount()}</span>
        )}
      </button>

      {/* Scroll to Top Button (Desktop Only) */}
      <button
        className={`scroll-to-top-button ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        ↑
      </button>

      {/* Success Notification */}
      {showNotification && (
        <div className="success-notification">
          <span className="notification-icon">✓</span>
          <span className="notification-text">{notificationMessage}</span>
        </div>
      )}

      {/* Variation Selection Modal */}
      {showVariationModal && selectedItem && (
        <VariationModal
          item={selectedItem}
          isOpen={showVariationModal}
          onClose={() => {
            setShowVariationModal(false);
            setSelectedItem(null);
            setSelectedVariations({ size: null, spice: null, addons: [] });
          }}
          onConfirm={handleVariationConfirm}
          selectedVariations={selectedVariations}
          onVariationChange={setSelectedVariations}
        />
      )}

    </div>
  );
};

export default Home;
